import {
  OPERATIONAL_VENDOR_ROWS,
  PROVIDER_CONTRACT_NOTICE,
  PROVIDER_LOCATION_NOTICE,
  SUBPROCESSOR_ROWS,
  UPSTREAM_PROVIDER_NOTICE,
  TRANSFER_UPDATE_DATE,
  VENDOR_REVIEW_DATE,
} from "./vendors";

export type Block =
  | {
      kind: "h1" | "meta" | "h2" | "h3" | "p" | "callout" | "ul-item";
      text: string;
    }
  | { kind: "table"; headers: string[]; rows: string[][] }
  | { kind: "contact" };

const TOS_VERSION = "1.4";
const TOS_EFFECTIVE = VENDOR_REVIEW_DATE;

export const TOS_BLOCKS: Block[] = [
  { kind: "h1", text: "Terms of Service" },
  { kind: "meta", text: `Version ${TOS_VERSION} · Effective ${TOS_EFFECTIVE}` },

  {
    kind: "p",
    text: `These Terms of Service ("Terms") govern your access to and use of condense.chat and any related APIs, applications, and websites (collectively, the "Service"). The Service is operated by UAB Imir Lab, a private limited company organized under the laws of the Republic of Lithuania, company code 307742103, with its registered office at V. Nagevičiaus 3g, Vilnius, Lithuania ("Imir Lab", "we", "our", or "us"). condense.chat is a brand and product of UAB Imir Lab.`,
  },
  {
    kind: "p",
    text: `By creating an account, accessing, or using the Service, you ("you", "Customer", or "User") agree to be bound by these Terms. If you do not agree, do not use the Service.`,
  },
  {
    kind: "p",
    text: `If you are entering into these Terms on behalf of a company, organization, or other legal entity, you represent that you have authority to bind that entity, and "you" refers to that entity.`,
  },

  { kind: "h2", text: "1. The Service" },
  {
    kind: "p",
    text: `condense.chat is a proxy and API service that compresses large language model ("LLM") inputs and forwards them to upstream LLM providers (such as Anthropic, OpenAI, Google, and others) according to your configuration. Requests may use credentials you provide or Condense-managed credentials where the selected service supports them. Condense may also use providers for internal compression as described in the Subprocessor List. The Service is designed to reduce input token consumption without modifying your underlying model or tooling.`,
  },
  {
    kind: "p",
    text: `We may modify, add, suspend, or remove features of the Service at any time. Material changes that adversely affect paid customers will be communicated by email with reasonable advance notice.`,
  },

  { kind: "h2", text: "2. Eligibility" },
  {
    kind: "p",
    text: `You must be at least 18 years old to use the Service. By using the Service, you confirm that you are 18 or older and that you have the legal capacity to enter into these Terms.`,
  },
  {
    kind: "p",
    text: `The Service is not directed to children. We do not knowingly collect personal data from anyone under 18.`,
  },

  { kind: "h2", text: "3. Account Registration" },
  {
    kind: "p",
    text: `To access most features, you must create an account. Authentication is handled via our identity provider (Clerk). You agree to:`,
  },
  {
    kind: "ul-item",
    text: "provide accurate, current, and complete information;",
  },
  {
    kind: "ul-item",
    text: "keep your credentials confidential and not share your account;",
  },
  {
    kind: "ul-item",
    text: "be responsible for all activity on your account; and",
  },
  {
    kind: "ul-item",
    text: "notify us promptly at team@condense.chat if you suspect unauthorized access.",
  },
  {
    kind: "p",
    text: `We may suspend or terminate accounts that violate these Terms or that we reasonably believe are being used fraudulently or for prohibited purposes.`,
  },

  { kind: "h2", text: "4. Plans, Credits, and Fees" },
  { kind: "h3", text: "4.1 Free starting credits" },
  {
    kind: "p",
    text: `New accounts receive USD 5 in starting credits to evaluate the Service. Free credits are non-transferable, non-refundable, have no cash value, and expire if not used within a reasonable period (which we may communicate at signup).`,
  },
  { kind: "h3", text: "4.2 Consumer plan (B2C)" },
  {
    kind: "p",
    text: `Individual users may subscribe to the consumer plan at USD 20 per month (or such other price as published at the time of signup). The subscription renews monthly until cancelled.`,
  },
  { kind: "h3", text: "4.3 Business plan (B2B)" },
  {
    kind: "p",
    text: `Business customers are billed for usage, on the basis and at the rates set out in an order form or pricing page. Volume-based and committed-use pricing is available on request.`,
  },
  { kind: "h3", text: "4.4 Payment" },
  {
    kind: "p",
    text: `Payments are processed by Stripe (or another payment processor we engage). You authorize us and our processor to charge your selected payment method for amounts due. If a charge fails, we may suspend the Service until payment is resolved.`,
  },
  {
    kind: "p",
    text: `All fees are exclusive of VAT and other applicable taxes, which are added where required by law.`,
  },
  { kind: "h3", text: "4.5 Price changes" },
  {
    kind: "p",
    text: `We may change our prices on at least 30 days' notice by email. Price changes apply to billing periods starting after the notice period. If you do not accept a price change, you may cancel before it takes effect.`,
  },

  { kind: "h2", text: "5. Consumer Right of Withdrawal (B2C only, EU/EEA)" },
  {
    kind: "p",
    text: `If you are a consumer resident in the EU/EEA, you have the right to withdraw from your subscription within 14 days of the start of your first paid subscription period, without giving any reason and without incurring any cost.`,
  },
  {
    kind: "p",
    text: `You can withdraw using the withdrawal function in your account ("Withdraw from contract"), which is available throughout the 14-day period and is at least as easy to use as signing up, or by sending a clear statement of your decision to team@condense.chat. We will acknowledge receipt of your withdrawal without undue delay on a durable medium (by email, stating the date and time we received it).`,
  },
  {
    kind: "p",
    text: `We will refund all payments received for the relevant subscription period within 14 days of being informed of your decision, using the same payment method you used for the original transaction, at no cost to you. Withdrawal is distinct from cancellation under Section 6: withdrawal unwinds the contract within the 14-day period and is refunded, whereas cancellation ends the subscription going forward.`,
  },
  { kind: "p", text: `This Section does not apply to business customers.` },

  { kind: "h2", text: "6. Cancellation and Termination" },
  {
    kind: "p",
    text: `You may cancel your subscription at any time from your account settings. Cancellation takes effect at the end of the current paid billing period; no pro-rata refunds are given except where required by law (including the right of withdrawal in Section 5).`,
  },
  {
    kind: "p",
    text: `We may suspend or terminate your access immediately if you breach these Terms, if continued provision would expose us or third parties to legal risk, or if required by law.`,
  },

  { kind: "h2", text: "7. Acceptable Use" },
  {
    kind: "p",
    text: `Your use of the Service must comply with our Acceptable Use Policy (the "AUP"), which is incorporated into these Terms by reference and forms part of the Agreement. The AUP describes the categories of conduct, content, and use cases that are not permitted on the Service, including illegal content, abuse, security probing, reverse-engineering, training of competing models, and unauthorised resale.`,
  },
  {
    kind: "p",
    text: `We may investigate suspected violations of the AUP and take any action we consider appropriate, including suspension, termination, and disclosure to law enforcement. We may update the AUP from time to time as set out in Section 17.`,
  },

  { kind: "h2", text: "8. Your Content and Your Credentials" },
  { kind: "h3", text: "8.1 Customer Inputs" },
  {
    kind: "p",
    text: `You retain all rights in the prompts, files, and other content you submit to the Service ("Customer Inputs"). You grant us a limited, worldwide, royalty-free licence to host, process, transmit, and compress Customer Inputs solely to provide the Service to you, subject to the privacy and training rules in Section 11 and our Privacy Policy.`,
  },
  { kind: "h3", text: "8.2 Outputs" },
  {
    kind: "p",
    text: `To the maximum extent permitted by law, we make no claim of ownership over the compressed outputs produced by the Service. Outputs from upstream LLM providers are governed by those providers' terms.`,
  },
  { kind: "h3", text: "8.3 Upstream provider credentials" },
  {
    kind: "p",
    text: `If you supply API keys or credentials for upstream LLM providers, you confirm you are authorised to use those credentials and to permit us to forward requests using them. You remain solely responsible for any charges those upstream providers bill you. We are not a party to your contract with any upstream LLM provider, and we are not responsible for their availability, output quality, or terms.`,
  },
  { kind: "h3", text: "8.4 Your representations" },
  {
    kind: "p",
    text: `You represent that you have all rights necessary to submit your Customer Inputs to the Service and that doing so does not violate any law, contract, or third-party right.`,
  },

  { kind: "h2", text: "9. Our Intellectual Property" },
  {
    kind: "p",
    text: `The Service, including its software, APIs, documentation, models, designs, branding, and the compression techniques embodied in it, is owned by Imir Lab or its licensors and is protected by intellectual property laws. No rights are granted to you except as expressly set out in these Terms. All other rights are reserved.`,
  },
  {
    kind: "p",
    text: `You may not use our trademarks, logos, or brand assets without our prior written consent, except to identify Imir Lab or condense.chat as a service you use.`,
  },

  { kind: "h2", text: "10. Feedback" },
  {
    kind: "p",
    text: `If you send us feedback, suggestions, or ideas about the Service, you grant us a perpetual, irrevocable, worldwide, royalty-free, sublicensable licence to use that feedback for any purpose, without obligation to you.`,
  },

  { kind: "h2", text: "11. Training and Data Use — Important Disclosure" },
  {
    kind: "p",
    text: `Our processing of your data is governed by our Privacy Policy and, for business customers, our Data Processing Addendum. The key rules are summarised here for transparency.`,
  },
  {
    kind: "p",
    text: `The same rules apply to every account, whether free or paid:`,
  },
  { kind: "h3", text: "(a) We never train on your Content Data" },
  {
    kind: "p",
    text: `We do not use your Customer Inputs, prompts, completions, or related Content Data to train, fine-tune, or evaluate our models — ever, on any account. There is nothing to opt out of, because training on your data is off by default and cannot be turned on.`,
  },
  { kind: "h3", text: "(b) Content Data is deleted within 7 days by default" },
  {
    kind: "p",
    text: `Unless Zero Data Retention is on (see (c)), we retain your Content Data for no more than 7 days for security, debugging, and abuse prevention, after which it is deleted from operational stores. Operational metadata (token counts, latencies, error codes, and similar aggregatable signals, stripped of content-derived identifiers) is retained for up to 90 days so we can run and bill the Service.`,
  },
  { kind: "h3", text: "(c) Zero Data Retention — never stored at all" },
  {
    kind: "p",
    text: `Zero Data Retention ("**ZDR**") is available on every account at no extra cost. With ZDR on, Content Data is not written to any durable store: each request is compressed in memory and discarded once your response is returned. The only thing that survives between turns is an ephemeral, hash-keyed compression index held in a memory-only cache that expires after 24 hours of inactivity and contains no readable conversation. Operational metadata needed to bill and secure the Service is still recorded. You can turn ZDR on for your account or for an individual API key from the dashboard, and business customers can enforce it for every member of their organisation.`,
  },
  {
    kind: "callout",
    text: `We never train on your data. Content Data is deleted within 7 days by default, and with Zero Data Retention on it is never stored at all — the same on every account, free or paid.`,
  },
  {
    kind: "callout",
    text: `Our compression models are not designed to handle special categories of personal data within the meaning of Article 9 GDPR (including data revealing racial or ethnic origin, political opinions, religious or philosophical beliefs, trade-union membership, genetic or biometric data, health data, or data concerning sex life or sexual orientation), or personal data of children. Please do not submit such data to the Service.`,
  },

  { kind: "h2", text: "12. No Reliance — High-Risk Uses" },
  {
    kind: "p",
    text: `The Service is provided as a tool for you to use at your own consideration. We make no representation or warranty that compressed outputs preserve the semantics, factual accuracy, completeness, or safety of the original inputs, or that upstream LLM responses produced from compressed inputs are accurate, lawful, or fit for any purpose.`,
  },
  {
    kind: "p",
    text: `You must not rely on the Service for any decision or use case where errors, omissions, or distortions could cause harm to people, property, finances, or rights, including without limitation: medical, psychiatric, or mental-health decisions; legal advice; safety-critical systems; financial trading or credit decisions; employment decisions; or any other high-risk application. If you choose to use the Service for such purposes, you do so entirely at your own risk and on your own consideration.`,
  },

  { kind: "h2", text: "13. Service Levels" },
  {
    kind: "p",
    text: `Unless we have agreed a separate service level agreement with you in a written order form, the Service is provided on an "as available" basis without uptime commitments. We aim for high availability but do not guarantee uninterrupted access.`,
  },

  { kind: "h2", text: "14. Disclaimers" },
  {
    kind: "p",
    text: `To the maximum extent permitted by law, the Service is provided "as is" and "as available" without warranties of any kind, whether express, implied, statutory, or otherwise, including any warranties of merchantability, fitness for a particular purpose, non-infringement, accuracy, or freedom from errors or harmful components.`,
  },
  {
    kind: "p",
    text: `We do not warrant that the Service will meet your requirements, be uninterrupted, secure, or error-free, or that defects will be corrected. We do not warrant the accuracy, reliability, or quality of any output, including any output from upstream LLM providers.`,
  },
  {
    kind: "p",
    text: `Nothing in this Section limits any non-excludable consumer warranties applicable to you under Lithuanian or EU law.`,
  },

  { kind: "h2", text: "15. Limitation of Liability" },
  { kind: "p", text: `To the maximum extent permitted by law:` },
  {
    kind: "ul-item",
    text: "Neither party will be liable to the other for any indirect, incidental, consequential, special, or punitive damages, loss of profits, revenue, business, goodwill, or data, even if advised of the possibility of such damages.",
  },
  {
    kind: "ul-item",
    text: "Each party's total aggregate liability arising out of or in connection with these Terms or the Service is limited to the greater of (i) the fees paid by you to us in the twelve (12) months preceding the event giving rise to the claim, or (ii) EUR 100.",
  },
  {
    kind: "ul-item",
    text: "Nothing in these Terms limits or excludes liability that cannot be limited or excluded under applicable law, including liability for death or personal injury caused by negligence, fraud, or wilful misconduct, or non-excludable consumer rights.",
  },

  { kind: "h2", text: "16. Indemnification" },
  {
    kind: "p",
    text: `You agree to indemnify and hold harmless Imir Lab, its affiliates, and its officers, employees, and agents from and against any claims, damages, losses, liabilities, and expenses (including reasonable legal fees) arising out of or related to: (a) your breach of these Terms; (b) your Customer Inputs; (c) your use of the Service in violation of law or third-party rights; or (d) your violation of any upstream LLM provider's policies.`,
  },
  {
    kind: "p",
    text: `This Section does not apply to consumers (B2C) to the extent prohibited by mandatory consumer law.`,
  },

  { kind: "h2", text: "17. Changes to These Terms" },
  {
    kind: "p",
    text: `We may update these Terms from time to time. If a change is material, we will notify you by email or through the Service at least 30 days before it takes effect. Your continued use of the Service after the effective date constitutes acceptance of the updated Terms. If you do not accept the changes, you must stop using the Service before they take effect.`,
  },

  { kind: "h2", text: "18. Governing Law and Disputes" },
  {
    kind: "p",
    text: `These Terms are governed by the laws of the Republic of Lithuania, without regard to conflict-of-laws principles. The UN Convention on Contracts for the International Sale of Goods does not apply.`,
  },
  {
    kind: "p",
    text: `Any dispute arising out of or in connection with these Terms or the Service will be brought exclusively before the competent courts of Vilnius, Lithuania, except that, if you are a consumer resident in the EU/EEA, you may also bring proceedings in the courts of your country of habitual residence.`,
  },
  {
    kind: "p",
    text: `EU consumers may use the European Commission's online dispute resolution platform at https://ec.europa.eu/consumers/odr.`,
  },

  { kind: "h2", text: "19. Miscellaneous" },
  {
    kind: "p",
    text: `Entire agreement. These Terms, together with the Privacy Policy, any DPA, and any order form, constitute the entire agreement between you and us regarding the Service and supersede all prior or contemporaneous agreements.`,
  },
  {
    kind: "p",
    text: `Severability. If any provision is held unenforceable, the remainder of these Terms remains in effect.`,
  },
  {
    kind: "p",
    text: `No waiver. Failure to enforce any provision is not a waiver of our right to do so later.`,
  },
  {
    kind: "p",
    text: `Assignment. You may not assign these Terms without our prior written consent. We may assign these Terms to an affiliate or in connection with a merger, acquisition, or sale of assets.`,
  },
  {
    kind: "p",
    text: `Force majeure. Neither party is liable for delays or failures caused by events beyond its reasonable control.`,
  },
  {
    kind: "p",
    text: `Notices. We may send notices to you by email or through the Service. You may contact us at team@condense.chat.`,
  },
  {
    kind: "p",
    text: `Language. These Terms are concluded in English. Any translation is for convenience only; the English version controls.`,
  },

  { kind: "h2", text: "20. Contact" },
  { kind: "contact" },
];

const PRIVACY_VERSION = "1.7";
const PRIVACY_EFFECTIVE = TRANSFER_UPDATE_DATE;

export const PRIVACY_BLOCKS: Block[] = [
  { kind: "h1", text: "Privacy Policy" },
  {
    kind: "meta",
    text: `Version ${PRIVACY_VERSION} · Effective ${PRIVACY_EFFECTIVE}`,
  },

  {
    kind: "p",
    text: `This Privacy Policy explains how **UAB Imir Lab** ("**Imir Lab**", "**we**", "**us**", or "**our**") collects and uses personal data in connection with **condense.chat** and our related websites, APIs, and applications (the "**Service**").`,
  },
  {
    kind: "p",
    text: `condense.chat is a brand and product of UAB Imir Lab. We are the **data controller** for personal data described in this Policy, except where we act as a **data processor** on behalf of a business customer (see Section 13).`,
  },
  {
    kind: "p",
    text: `If you have any questions, contact us at **team@condense.chat**.`,
  },

  { kind: "h2", text: "1. Who we are" },
  { kind: "contact" },

  { kind: "h2", text: "2. Scope" },
  {
    kind: "p",
    text: `This Policy applies to personal data we process when you:`,
  },
  { kind: "ul-item", text: "visit our websites (including condense.chat);" },
  { kind: "ul-item", text: "create or use an account on the Service;" },
  { kind: "ul-item", text: "send requests through our API or proxy;" },
  { kind: "ul-item", text: "communicate with us by email or other means." },
  {
    kind: "p",
    text: `It does **not** cover third-party websites, products, or services that we link to but do not control, including upstream LLM providers you route through the Service.`,
  },

  { kind: "h2", text: "3. Personal data we collect" },
  { kind: "p", text: `We collect the following categories of personal data:` },

  { kind: "h3", text: "3.1 Account data" },
  {
    kind: "p",
    text: `When you sign up, our identity provider Clerk handles authentication and stores your account credentials. We receive your **email address**, **name (if provided)**, **user ID**, and **authentication metadata** (such as login timestamps, IP address of login, and authentication method used).`,
  },

  { kind: "h3", text: "3.2 Billing data" },
  {
    kind: "p",
    text: `When you subscribe to a paid plan or purchase credits, Stripe collects and processes your **payment method details**, **billing address**, and **transaction history**. We do not receive your full card number. We receive customer and subscription identifiers, transaction status and billing information needed to manage your purchases.`,
  },

  { kind: "h3", text: "3.3 Content data — your prompts and outputs" },
  {
    kind: "p",
    text: `When you use the Service, we process the **prompts, files, completions, and metadata** that pass through our compression proxy ("**Content Data**"). We never use Content Data for training. By default we delete it within 7 days, and with **Zero Data Retention** on we never store it at all — the same on every account. See Section 6.`,
  },

  { kind: "h3", text: "3.4 Usage and technical data" },
  {
    kind: "p",
    text: `We collect technical and usage information automatically, including:`,
  },
  { kind: "ul-item", text: "IP address (truncated where feasible);" },
  {
    kind: "ul-item",
    text: "request timestamps, endpoint paths, request/response sizes, latency;",
  },
  {
    kind: "ul-item",
    text: "token counts and compression ratios (for billing and analytics);",
  },
  { kind: "ul-item", text: "HTTP user-agent string;" },
  {
    kind: "ul-item",
    text: "error and diagnostic logs (collected via our logging and metrics stack, Loki and Prometheus).",
  },

  { kind: "h3", text: "3.5 Website and product analytics" },
  {
    kind: "p",
    text: `On our marketing website and signed-in app, we use **PostHog** for optional browser analytics. Browser analytics are **off by default** and run only after you consent through our cookie banner. If allowed, PostHog sets a first-party identifier cookie across our subdomains and collects page views, referrer, source IP address and derived location, browser and device information, feature interactions, clicks, and scrolling. Once you sign in, we associate that activity with your account (including your email, name, and user ID) so we can understand which visits convert to accounts and how signed-up users use the Service.`,
  },
  {
    kind: "p",
    text: `**Session replay is a separate optional choice.** If you allow it, PostHog also receives a visual reconstruction of the pages used, including clicks and scrolling. Replay requires analytics to be enabled, but you can allow analytics without allowing replay. Form inputs are masked, email addresses displayed as page text are redacted in your browser, one-time API secrets are excluded, and URL query strings and fragments are removed before capture. These controls run before replay data is sent to PostHog. We do not intentionally record passwords, API keys, authentication tickets, prompt content, or form values.`,
  },
  {
    kind: "p",
    text: `If you choose **just essentials**, PostHog is not initialised on later page loads and no new PostHog cookie is set. If it was already running, capture and persistence are disabled immediately. Your choices are stored for 180 days in the strictly necessary **cc_cookie_preferences** cookie. You can accept all, reject all optional cookies, or choose analytics and replay separately. You can change or withdraw your choice at any time using the **Cookie settings** button in our Cookie Disclosure.`,
  },
  {
    kind: "p",
    text: `Browser analytics traffic is routed through our own domain (a first-party proxy) and on to PostHog Cloud EU. Separately, our backend sends account-registration events and links account identifiers in PostHog independently of the browser cookie choice. Registration events can include your email address, account identifiers, registration time and signup method. These backend events do not enable browser analytics or session replay. PostHog is listed as a service provider (see Section 8.1).`,
  },

  { kind: "h3", text: "3.6 Communications" },
  {
    kind: "p",
    text: `If you contact us, we keep a record of the messages and any attachments you send, along with your contact details. We use Google email services for outgoing messages. Our website also links to Cal.com and Calendly for bookings; information you submit there is handled by those services under their own terms. Pages can load fonts from Google Fonts, which receives browser request metadata.`,
  },

  { kind: "h2", text: "4. Where we get personal data" },
  { kind: "p", text: `We collect personal data:` },
  {
    kind: "ul-item",
    text: "directly from you, when you sign up, configure your account, send requests, or communicate with us;",
  },
  {
    kind: "ul-item",
    text: "automatically, when you use the Service (technical and usage data); and",
  },
  {
    kind: "ul-item",
    text: "from our service providers (for example, authentication metadata from Clerk, billing data from Stripe).",
  },

  { kind: "h2", text: "5. Why we use personal data, and our lawful bases" },
  {
    kind: "table",
    headers: ["Purpose", "Categories used", "Lawful basis (GDPR Art. 6)"],
    rows: [
      [
        "Providing the Service (compressing prompts, forwarding to upstream LLMs)",
        "Content Data, Account Data, Usage Data",
        "Performance of contract (Art. 6(1)(b))",
      ],
      [
        "Account management, authentication, support",
        "Account Data, Communications",
        "Performance of contract (Art. 6(1)(b))",
      ],
      [
        "Billing, fraud prevention",
        "Billing Data, Account Data",
        "Performance of contract; legal obligation (Art. 6(1)(b), (c))",
      ],
      [
        "Service security, abuse prevention, debugging, capacity planning",
        "Usage Data, Technical Data, IP address",
        "Legitimate interest in operating a secure, reliable service (Art. 6(1)(f))",
      ],
      [
        "Improving our compression models",
        "Usage Data only — never Content Data, on any account",
        "Legitimate interest in developing and improving our products (Art. 6(1)(f)). We never use Content Data to train, fine-tune, or evaluate models on any account",
      ],
      [
        "Service announcements and transactional emails",
        "Account Data",
        "Performance of contract (Art. 6(1)(b))",
      ],
      [
        "Marketing emails (product updates, newsletters)",
        "Account Data",
        "Legitimate interest (Art. 6(1)(f)); easy opt-out provided in every message",
      ],
      [
        "Legal and regulatory compliance, defending claims",
        "All categories as needed",
        "Legal obligation; legitimate interest (Art. 6(1)(c), (f))",
      ],
    ],
  },
  {
    kind: "p",
    text: `You can object to processing based on legitimate interests at any time (see Section 11).`,
  },

  {
    kind: "h2",
    text: "6. Training, Zero Data Retention, and Content Data retention — important disclosure",
  },
  {
    kind: "p",
    text: `Three rules govern your Content Data: we never train on it, you can choose to have it never stored at all (Zero Data Retention), and otherwise it is deleted within 7 days. The same rules apply to every account, free or paid.`,
  },

  { kind: "h3", text: "6.1 We never train on your Content Data" },
  {
    kind: "p",
    text: `**We do not use your prompts, completions, or related Content Data to train, fine-tune, or evaluate our models — ever, on any account.** Training on Content Data is off by default and cannot be switched on, so there is nothing you need to opt out of.`,
  },
  {
    kind: "p",
    text: `Where we improve our compression models, we rely on de-identified, aggregated Usage Data (such as token counts and latencies) — never your Content Data. The lawful basis for processing that operational metadata is **legitimate interest** in operating and improving a reliable Service.`,
  },
  {
    kind: "callout",
    text: `Sensitive data. Our compression models are not designed to process special categories of personal data (within the meaning of Article 9 GDPR — for example, data revealing health, ethnicity, political opinions, religion, sexual orientation, or biometric/genetic identifiers) or personal data of children. Please do not submit such data to the Service.`,
  },

  { kind: "h3", text: "6.2 Zero Data Retention (ZDR)" },
  {
    kind: "p",
    text: `**Zero Data Retention is available on every account at no extra cost.** With ZDR on, we do not write your Content Data to any durable store: each request is compressed in memory and discarded once your response is returned. The only thing that survives between turns is an ephemeral, hash-keyed compression index held in a memory-only cache that expires after **24 hours** of inactivity and contains no readable conversation.`,
  },
  {
    kind: "p",
    text: `Operational metadata (Section 6.3) is still recorded under ZDR so we can bill and secure the Service. You can turn ZDR on for your account or for an individual API key from the dashboard. Business customers can enforce ZDR for every member of their organisation, in which case individual members cannot turn it off.`,
  },
  {
    kind: "p",
    text: `Because nothing is stored, ZDR also means we cannot show you session history or recover a conversation for you. The lawful basis for the residual processing under ZDR is the same as for the Service generally (Section 5).`,
  },

  { kind: "h3", text: "6.3 Content Data is deleted within 7 days by default" },
  {
    kind: "p",
    text: `When ZDR is off, we retain your Content Data for **no more than 7 days** for security, debugging, and abuse prevention, after which it is deleted from operational stores. This applies to every account, free or paid.`,
  },
  {
    kind: "p",
    text: `Operational metadata derived from your requests (token counts, latencies, error codes, and similar aggregatable signals, stripped of content-derived identifiers) is retained for **up to 90 days** so we can run, secure, and bill the Service.`,
  },

  { kind: "h3", text: "6.4 Account, billing, and usage data" },
  {
    kind: "p",
    text: `Account data and billing records are retained for the life of your account plus a reasonable period after termination (typically **10 years** for accounting records as required by Lithuanian law, and otherwise no longer than necessary). Operational logs are retained for **up to 90 days**.`,
  },

  { kind: "h3", text: "6.5 Deletion" },
  {
    kind: "p",
    text: `You can request deletion of your account and associated data at any time by emailing **team@condense.chat** (see Section 11). We may retain certain data where required by law (for example, invoicing records) or where necessary to defend legal claims.`,
  },

  { kind: "h2", text: "7. Cookies" },
  {
    kind: "p",
    text: `We use **strictly necessary cookies** for authentication and session security, which do not require consent. We also use **analytics cookies** (PostHog) — but only if you accept them through our cookie banner. Until you consent, no analytics cookies are set. We do not use advertising cookies or cross-site tracking.`,
  },
  { kind: "p", text: `For details, see our **Cookie Disclosure**.` },

  {
    kind: "h2",
    text: "8. Sharing with third parties — our sub-processors and partners",
  },
  {
    kind: "p",
    text: `We share personal data with the following categories of recipients:`,
  },

  { kind: "h3", text: "8.1 Sub-processors" },
  {
    kind: "p",
    text: `We use AWS for application infrastructure, Clerk for authentication, Stripe for payments, Google for email and enabled Gemini incident analysis, Anthropic for enabled internal compression, and PostHog for analytics and account-registration events. Operational alerts and reports are sent to Slack; incident tickets can be sent to Linear when configured. These flows may include account data, conversation content for compression, or operational logs and diagnostics, according to the service used. Providers processing Customer Personal Data on our behalf are subject to our DPA's subprocessor obligations.`,
  },
  {
    kind: "p",
    text: `Our **Subprocessor List** describes these services, processing-location information and additional operational providers. Processing locations depend on the service and account configuration; our EU application hosting does not establish EU-only processing by every provider.`,
  },

  { kind: "h3", text: "8.2 Upstream LLM providers" },
  {
    kind: "p",
    text: UPSTREAM_PROVIDER_NOTICE,
  },

  { kind: "h3", text: "8.3 Other recipients" },
  { kind: "p", text: `We may also share personal data with:` },
  {
    kind: "ul-item",
    text: "professional advisers (lawyers, accountants, auditors) under confidentiality obligations;",
  },
  {
    kind: "ul-item",
    text: "public authorities, where required by law or in response to lawful requests; and",
  },
  {
    kind: "ul-item",
    text: "an acquirer or successor in connection with a merger, acquisition, or sale of assets, subject to confidentiality and continued compliance with this Policy.",
  },
  { kind: "p", text: `We do **not** sell your personal data.` },

  { kind: "h2", text: "9. International data transfers" },
  {
    kind: "p",
    text: `Our application infrastructure is hosted in **AWS Europe (Spain)**. If you send requests to **api.us.condense.chat**, they are received and processed on AWS in the **United States** (us-east-1); conversation content is held in memory only and sent to our EU infrastructure for compression, and account, billing and administrative data stay in the EU. Several of our sub-processors, including Anthropic, Clerk, Stripe, Google, Slack and Linear, process personal data in the United States; the Subprocessor List names the location and safeguard for each. Transfers must be covered by an applicable legal basis and appropriate safeguards under GDPR Chapter V, which may include:`,
  },
  {
    kind: "ul-item",
    text: "the European Commission's Standard Contractual Clauses (2021/914) for transfers to non-adequate countries;",
  },
  {
    kind: "ul-item",
    text: "the UK International Data Transfer Addendum for transfers from the UK; and",
  },
  {
    kind: "ul-item",
    text: "additional technical and organisational measures, including encryption in transit and at rest.",
  },
  {
    kind: "p",
    text: `For transfers to U.S. sub-processors that are certified under the **EU-U.S. Data Privacy Framework**, we may also rely on that framework where applicable.`,
  },
  {
    kind: "p",
    text: `You can request a copy of the safeguards we use by emailing **team@condense.chat**.`,
  },

  { kind: "h2", text: "10. Security" },
  {
    kind: "p",
    text: `We maintain technical and organisational measures appropriate to the risk of the processing, including:`,
  },
  { kind: "ul-item", text: "TLS encryption for data in transit;" },
  { kind: "ul-item", text: "encryption at rest for stored data;" },
  {
    kind: "ul-item",
    text: "principle of least privilege for internal access;",
  },
  {
    kind: "ul-item",
    text: "centralised logging and monitoring (Loki and Prometheus);",
  },
  {
    kind: "ul-item",
    text: "regular dependency updates and vulnerability monitoring;",
  },
  { kind: "ul-item", text: "incident response procedures." },
  {
    kind: "p",
    text: `No system is perfectly secure. If you become aware of a security issue, contact us at **team@condense.chat**.`,
  },

  { kind: "h2", text: "11. Your rights under the GDPR" },
  {
    kind: "p",
    text: `If you are in the EU/EEA, the UK, or Switzerland, you have the following rights regarding your personal data:`,
  },
  {
    kind: "ul-item",
    text: "**Access** — obtain confirmation of whether we process your personal data, and a copy of it;",
  },
  {
    kind: "ul-item",
    text: "**Rectification** — have inaccurate data corrected;",
  },
  {
    kind: "ul-item",
    text: `**Erasure** ("right to be forgotten") — have your personal data deleted, subject to legal exceptions;`,
  },
  {
    kind: "ul-item",
    text: "**Restriction** — limit how we process your personal data in certain circumstances;",
  },
  {
    kind: "ul-item",
    text: "**Portability** — receive your personal data in a structured, commonly used, machine-readable format;",
  },
  {
    kind: "ul-item",
    text: "**Objection** — object to processing based on legitimate interests, including for direct marketing;",
  },
  {
    kind: "ul-item",
    text: "**Withdraw consent** — where processing is based on consent, withdraw it at any time (this does not affect prior lawful processing);",
  },
  {
    kind: "ul-item",
    text: "**Not be subject to automated decision-making** that has legal or similarly significant effects — we do not carry out such decision-making in the Service.",
  },
  {
    kind: "p",
    text: `To exercise any of these rights, email **team@condense.chat**. We will respond within one month (extendable by two months for complex requests). We may need to verify your identity before acting on a request.`,
  },
  {
    kind: "p",
    text: `If you believe we have not handled your data lawfully, you have the right to lodge a complaint with a supervisory authority. In Lithuania this is the **State Data Protection Inspectorate (Valstybinė duomenų apsaugos inspekcija)** — vdai.lrv.lt — or with the supervisory authority in your country of residence.`,
  },

  { kind: "h2", text: "12. Marketing communications" },
  {
    kind: "p",
    text: `We may send you marketing emails about new features, product updates, and content we think you'll find useful. **You can opt out at any time** by clicking the unsubscribe link in any marketing email, or by emailing **team@condense.chat**. Opting out of marketing does not stop transactional emails (receipts, security notices, service announcements) that are necessary to provide the Service.`,
  },

  { kind: "h2", text: "13. When we act as a data processor" },
  {
    kind: "p",
    text: `When you use the Service as a business customer to process personal data of your own end-users, employees, or other individuals, you are the **data controller** and we act as your **data processor**. In that role:`,
  },
  {
    kind: "ul-item",
    text: "our processing is governed by our Data Processing Addendum (DPA), which is automatically incorporated into our agreement with business customers;",
  },
  {
    kind: "ul-item",
    text: "the categories of personal data, data subjects, and processing details are described in the DPA;",
  },
  {
    kind: "ul-item",
    text: "you remain responsible for the lawfulness of the processing and for providing notice to your end-users.",
  },
  {
    kind: "p",
    text: `A signed DPA is available on request from **team@condense.chat**.`,
  },

  { kind: "h2", text: "14. Children" },
  {
    kind: "p",
    text: `The Service is not intended for users under **18**. We do not knowingly collect personal data from anyone under 18. If you believe a child has provided us with personal data, contact us at **team@condense.chat** and we will delete it.`,
  },

  { kind: "h2", text: "15. Changes to this Policy" },
  {
    kind: "p",
    text: `We may update this Policy from time to time. The "Effective date" at the top shows when it was last revised. If we make material changes, we will notify you by email or through the Service before the changes take effect. Your continued use of the Service after the effective date constitutes acceptance of the updated Policy.`,
  },

  { kind: "h2", text: "16. Contact" },
  {
    kind: "p",
    text: `For any privacy questions, requests, or complaints, contact us at:`,
  },
  { kind: "contact" },
];

const DPA_VERSION = "1.4";
const DPA_EFFECTIVE = TRANSFER_UPDATE_DATE;

export const DPA_BLOCKS: Block[] = [
  { kind: "h1", text: "Data Processing Addendum" },
  { kind: "meta", text: `Version ${DPA_VERSION} · Effective ${DPA_EFFECTIVE}` },

  {
    kind: "p",
    text: `This Data Processing Addendum ("**DPA**") forms part of the agreement between **UAB Imir Lab** ("**Imir Lab**", "**we**", "**us**", or "**Processor**") and the customer identified in the Agreement ("**Customer**", "**you**", or "**Controller**") for use of **condense.chat** and related services (the "**Services**"), as set out in our Terms of Service or in a written order form (the "**Agreement**").`,
  },
  {
    kind: "p",
    text: `This DPA reflects the parties' agreement on the processing of Customer Personal Data in connection with the Services. In the event of a conflict between this DPA and the Agreement, this DPA prevails to the extent of the conflict in respect of personal data matters.`,
  },

  { kind: "h2", text: "1. Definitions" },
  {
    kind: "p",
    text: `Capitalized terms not defined here have the meanings given in the Agreement or in applicable Data Protection Laws.`,
  },
  {
    kind: "p",
    text: `**"Customer Personal Data"** means Personal Data that Imir Lab Processes on behalf of Customer in connection with the Services, as described in **Schedule 1**.`,
  },
  {
    kind: "p",
    text: `**"Data Protection Laws"** means all laws and regulations applicable to the Processing of Personal Data under this DPA, including (a) Regulation (EU) 2016/679 (the "**GDPR**"); (b) the GDPR as incorporated into UK law (the "**UK GDPR**") and the UK Data Protection Act 2018; (c) the Swiss Federal Act on Data Protection (the "**Swiss FADP**"); and (d) the U.S. state privacy laws applicable to the Processing, including the California Consumer Privacy Act, as amended ("**CCPA**").`,
  },
  {
    kind: "p",
    text: `**"Personal Data"**, **"Process"**, **"Processing"**, **"Controller"**, **"Processor"**, **"Data Subject"**, **"Personal Data Breach"**, and **"Supervisory Authority"** have the meanings given in the GDPR (and equivalent terms under other Data Protection Laws have the same meaning).`,
  },
  {
    kind: "p",
    text: `**"EU SCCs"** means the standard contractual clauses approved by the European Commission in Implementing Decision (EU) 2021/914 of 4 June 2021.`,
  },
  {
    kind: "p",
    text: `**"UK Addendum"** means the International Data Transfer Addendum to the EU SCCs issued by the UK Information Commissioner, version B1.0.`,
  },
  {
    kind: "p",
    text: `**"Sub-Processor"** means any third party engaged by Imir Lab to Process Customer Personal Data on Customer's behalf.`,
  },
  {
    kind: "p",
    text: `**"Zero Data Retention"** or **"ZDR"** means the mode of the Services, available to every Customer at no additional charge and enforceable by Customer for its whole organisation, in which Content Data is Processed in memory only and is not written to any durable store, as described in Section 11.2 and Schedule 1.B.`,
  },
  {
    kind: "p",
    text: `**"Sub-Processor List"** means the list of Sub-Processors set out in **Schedule 3**, as updated from time to time.`,
  },

  { kind: "h2", text: "2. Scope, Roles, and Customer Instructions" },
  { kind: "h3", text: "2.1 Roles" },
  {
    kind: "p",
    text: `As between the parties, Customer is the **Controller** and Imir Lab is the **Processor** of Customer Personal Data. Where Customer is itself a Processor acting on behalf of another Controller, Imir Lab is a **Sub-Processor**.`,
  },
  { kind: "h3", text: "2.2 Subject matter and duration" },
  {
    kind: "p",
    text: `The subject matter of the Processing is Imir Lab's provision of the Services to Customer under the Agreement. The duration of the Processing is the term of the Agreement, plus any post-termination period required to return or delete Customer Personal Data under Section 11.`,
  },
  { kind: "h3", text: "2.3 Details of Processing" },
  {
    kind: "p",
    text: `The nature and purpose of the Processing, the types of Customer Personal Data, and the categories of Data Subjects are set out in **Schedule 1**.`,
  },
  { kind: "h3", text: "2.4 Customer instructions" },
  {
    kind: "p",
    text: `Imir Lab will Process Customer Personal Data only on Customer's documented instructions. The Agreement (including this DPA, our Privacy Policy, and any order form) and Customer's use of the Services constitute Customer's complete and final instructions. Any additional instructions require written agreement between the parties and may be subject to additional fees.`,
  },
  {
    kind: "p",
    text: `Imir Lab will inform Customer if, in its opinion, an instruction infringes Data Protection Laws, unless prohibited from doing so by law.`,
  },
  { kind: "h3", text: "2.5 Customer responsibilities" },
  { kind: "p", text: `Customer warrants and undertakes that:` },
  {
    kind: "ul-item",
    text: "it has all necessary rights and lawful bases to provide Customer Personal Data to Imir Lab for the Processing described in this DPA;",
  },
  {
    kind: "ul-item",
    text: "it has provided all required notices to Data Subjects and, where required, obtained their consents;",
  },
  {
    kind: "ul-item",
    text: "it will not submit special-category Personal Data (Article 9 GDPR), data relating to criminal convictions and offences (Article 10 GDPR), or Personal Data of children under the minimum age applicable, unless it has notified Imir Lab in advance and the parties have agreed on additional safeguards in writing; and",
  },
  {
    kind: "ul-item",
    text: "its instructions to Imir Lab comply with Data Protection Laws.",
  },

  { kind: "h2", text: "3. Personnel and Confidentiality" },
  {
    kind: "p",
    text: `Imir Lab will ensure that persons authorised to Process Customer Personal Data:`,
  },
  {
    kind: "ul-item",
    text: "have committed themselves to confidentiality or are under an appropriate statutory obligation of confidentiality;",
  },
  {
    kind: "ul-item",
    text: "Process Customer Personal Data only as needed to perform their duties under the Agreement; and",
  },
  {
    kind: "ul-item",
    text: "receive appropriate training on the handling of Personal Data.",
  },

  { kind: "h2", text: "4. Security" },
  { kind: "h3", text: "4.1 Security measures" },
  {
    kind: "p",
    text: `Imir Lab will implement and maintain appropriate technical and organisational measures designed to protect Customer Personal Data against unauthorised or unlawful Processing and against accidental loss, destruction, damage, alteration, or disclosure. A description of these measures is set out in **Schedule 2**.`,
  },
  { kind: "h3", text: "4.2 Updates" },
  {
    kind: "p",
    text: `Imir Lab may update its security measures from time to time, provided that any update does not materially diminish the overall level of protection.`,
  },

  { kind: "h2", text: "5. Sub-Processors" },
  { kind: "h3", text: "5.1 General authorisation" },
  {
    kind: "p",
    text: `Customer provides Imir Lab with **general written authorisation** to engage Sub-Processors for the Processing of Customer Personal Data, subject to this Section 5.`,
  },
  { kind: "h3", text: "5.2 Current Sub-Processors" },
  {
    kind: "p",
    text: `The current list of Sub-Processors is set out in **Schedule 3** and is also available on request from **team@condense.chat**.`,
  },
  { kind: "h3", text: "5.3 New Sub-Processors" },
  {
    kind: "p",
    text: `Imir Lab will give Customer at least **30 days' prior notice** of the addition of any new Sub-Processor (by email, through the Services, or by updating the Sub-Processor List on a public page that Customer can subscribe to). Customer may object to a new Sub-Processor on reasonable data-protection grounds by emailing **team@condense.chat** within the notice period, in which case the parties will work together in good faith to find an alternative. If no resolution is reached within a reasonable time, Customer may terminate the affected portion of the Services and receive a pro-rata refund of any prepaid, unused fees.`,
  },
  { kind: "h3", text: "5.4 Sub-Processor obligations" },
  {
    kind: "p",
    text: `Imir Lab will enter into a written agreement with each Sub-Processor that imposes data-protection obligations no less protective than those in this DPA. Imir Lab remains liable to Customer for the performance of each Sub-Processor's obligations under this DPA, subject to the limitations of liability in the Agreement.`,
  },

  { kind: "h2", text: "6. Data Subject Rights" },
  { kind: "h3", text: "6.1 Assistance to Customer" },
  {
    kind: "p",
    text: `Taking into account the nature of the Processing, Imir Lab will provide reasonable assistance to Customer (including by appropriate technical and organisational measures) to fulfil Customer's obligations to respond to requests from Data Subjects exercising their rights under Data Protection Laws (access, rectification, erasure, restriction, portability, objection, and rights related to automated decision-making).`,
  },
  { kind: "h3", text: "6.2 Direct requests to Imir Lab" },
  {
    kind: "p",
    text: `If Imir Lab receives a request from a Data Subject relating to Customer Personal Data, it will:`,
  },
  {
    kind: "ul-item",
    text: "not respond directly to the Data Subject (except to acknowledge receipt and confirm that the request should be directed to Customer);",
  },
  { kind: "ul-item", text: "promptly forward the request to Customer; and" },
  {
    kind: "ul-item",
    text: "follow Customer's reasonable instructions on how to respond.",
  },
  { kind: "h3", text: "6.3 Fees" },
  {
    kind: "p",
    text: `Imir Lab may charge Customer reasonable fees for assistance under this Section to the extent permitted by Data Protection Laws and the Agreement.`,
  },

  { kind: "h2", text: "7. Audits and Compliance" },
  { kind: "h3", text: "7.1 Information" },
  {
    kind: "p",
    text: `Imir Lab will make available to Customer, on reasonable request, the information necessary to demonstrate compliance with its obligations under Article 28 GDPR and this DPA.`,
  },
  { kind: "h3", text: "7.2 Audit rights" },
  {
    kind: "p",
    text: `Customer may, on at least **30 days' prior written notice** and no more than **once per calendar year** (or more frequently if required by a Supervisory Authority or following a Personal Data Breach affecting Customer), audit Imir Lab's compliance with this DPA, either by:`,
  },
  {
    kind: "ul-item",
    text: "reviewing Imir Lab's then-current third-party audit reports, certifications, and policies (where Imir Lab makes such reports available); or",
  },
  {
    kind: "ul-item",
    text: "where the materials in (a) are insufficient, conducting an on-site or remote audit during normal business hours.",
  },
  { kind: "p", text: `Any audit must:` },
  {
    kind: "ul-item",
    text: "be conducted in a manner that is minimally disruptive to Imir Lab's business;",
  },
  {
    kind: "ul-item",
    text: "not extend to the data, premises, systems, or personnel of any other customer of Imir Lab;",
  },
  {
    kind: "ul-item",
    text: "be subject to reasonable confidentiality obligations; and",
  },
  {
    kind: "ul-item",
    text: "be at Customer's cost, unless the audit reveals material non-compliance, in which case Imir Lab will bear reasonable audit costs.",
  },
  { kind: "h3", text: "7.3 Supervisory Authority audits" },
  {
    kind: "p",
    text: `Imir Lab will cooperate with audits conducted by, or on behalf of, a Supervisory Authority where required by Data Protection Laws.`,
  },

  { kind: "h2", text: "8. Personal Data Breach" },
  { kind: "h3", text: "8.1 Notification" },
  {
    kind: "p",
    text: `Imir Lab will notify Customer **without undue delay**, and in any event within **72 hours**, after becoming aware of a Personal Data Breach affecting Customer Personal Data.`,
  },
  { kind: "h3", text: "8.2 Information provided" },
  {
    kind: "p",
    text: `To the extent known at the time of notification, Imir Lab will provide Customer with:`,
  },
  {
    kind: "ul-item",
    text: "a description of the nature of the Personal Data Breach, including (where possible) the categories and approximate number of Data Subjects and Personal Data records affected;",
  },
  {
    kind: "ul-item",
    text: "the likely consequences of the Personal Data Breach;",
  },
  {
    kind: "ul-item",
    text: "the measures taken or proposed to address the Personal Data Breach and mitigate its possible adverse effects; and",
  },
  {
    kind: "ul-item",
    text: "the name and contact details of the relevant point of contact at Imir Lab.",
  },
  {
    kind: "p",
    text: `Imir Lab will provide updates as further information becomes available.`,
  },
  { kind: "h3", text: "8.3 No admission" },
  {
    kind: "p",
    text: `Imir Lab's notification of, or response to, a Personal Data Breach under this Section is not, and will not be construed as, an admission of fault or liability.`,
  },

  {
    kind: "h2",
    text: "9. Data Protection Impact Assessments and Prior Consultation",
  },
  {
    kind: "p",
    text: `Imir Lab will provide reasonable assistance to Customer with any data protection impact assessments and prior consultations with Supervisory Authorities required under Articles 35 and 36 GDPR, in each case solely in relation to Processing of Customer Personal Data by Imir Lab and taking into account the nature of the Processing and the information available to Imir Lab.`,
  },

  { kind: "h2", text: "10. International Transfers" },
  { kind: "h3", text: "10.1 General" },
  {
    kind: "p",
    text: `Where Imir Lab Processes Customer Personal Data outside the European Economic Area, the United Kingdom, or Switzerland, the parties agree to comply with the transfer mechanisms set out in this Section.`,
  },
  { kind: "h3", text: "10.2 EU SCCs" },
  {
    kind: "p",
    text: `To the extent the Processing involves a transfer of Customer Personal Data subject to GDPR from Customer (or its end-Controller) to Imir Lab in a country not deemed adequate by the European Commission, the **EU SCCs** are incorporated into this DPA by reference and apply as follows:`,
  },
  {
    kind: "ul-item",
    text: "**Module Two** (Controller to Processor) applies where Customer is a Controller and Imir Lab is a Processor;",
  },
  {
    kind: "ul-item",
    text: "**Module Three** (Processor to Sub-Processor) applies where Customer is a Processor and Imir Lab is a Sub-Processor;",
  },
  { kind: "p", text: `For each Module, where applicable:` },
  {
    kind: "ul-item",
    text: "the optional docking clause in Clause 7 does not apply;",
  },
  {
    kind: "ul-item",
    text: "in Clause 9, Option 2 (general written authorisation) applies, with the notice period in Section 5.3 of this DPA;",
  },
  {
    kind: "ul-item",
    text: "the optional language in Clause 11 does not apply;",
  },
  { kind: "ul-item", text: "all square brackets in Clause 13 are removed;" },
  {
    kind: "ul-item",
    text: "in Clause 17 (Option 1), the EU SCCs are governed by the **laws of the Republic of Lithuania**;",
  },
  {
    kind: "ul-item",
    text: "in Clause 18(b), disputes will be resolved before the courts of **Vilnius, Lithuania**;",
  },
  {
    kind: "ul-item",
    text: "Schedule 1 to this DPA contains the information required in Annex I and Annex III of the EU SCCs;",
  },
  {
    kind: "ul-item",
    text: "Schedule 2 to this DPA contains the information required in Annex II of the EU SCCs.",
  },
  { kind: "h3", text: "10.3 UK Addendum" },
  {
    kind: "p",
    text: `To the extent the Processing involves a transfer of Customer Personal Data subject to the UK GDPR, the **UK Addendum** is incorporated into this DPA by reference. Part 1 of the UK Addendum is completed using the information in this DPA and in Schedules 1–3, and Part 2 (the Mandatory Clauses) applies as drafted. Either party may end the UK Addendum in accordance with Section 19 of the Mandatory Clauses.`,
  },
  { kind: "h3", text: "10.4 Swiss FADP" },
  {
    kind: "p",
    text: `To the extent the Processing involves a transfer of Customer Personal Data subject to the Swiss FADP, the EU SCCs apply with the following modifications:`,
  },
  {
    kind: "ul-item",
    text: "references to the GDPR are read as references to the Swiss FADP;",
  },
  {
    kind: "ul-item",
    text: "references to EU Member State law are read as references to Swiss law;",
  },
  {
    kind: `ul-item`,
    text: `the term "Member State" must not be interpreted in such a way as to exclude Data Subjects in Switzerland from the possibility of suing for their rights;`,
  },
  {
    kind: "ul-item",
    text: "the competent Supervisory Authority is the Swiss Federal Data Protection and Information Commissioner;",
  },
  {
    kind: "ul-item",
    text: "the SCCs are governed by Swiss law and disputes are resolved by the Swiss courts.",
  },
  { kind: "h3", text: "10.5 Alternative mechanisms" },
  {
    kind: "p",
    text: `If a transfer mechanism in this Section becomes invalid or insufficient, the parties will cooperate in good faith to put in place an alternative mechanism that complies with Data Protection Laws, including (where applicable) the EU-U.S. Data Privacy Framework or successor frameworks.`,
  },

  { kind: "h2", text: "11. Return or Deletion of Customer Personal Data" },
  { kind: "h3", text: "11.1 On termination" },
  {
    kind: "p",
    text: `On termination or expiry of the Agreement, Imir Lab will (at Customer's choice) delete or return all Customer Personal Data in its possession or control, and will procure that any Sub-Processor does the same, within **30 days** of termination, unless retention is required by law.`,
  },
  { kind: "h3", text: "11.2 Ongoing operations" },
  {
    kind: "p",
    text: `For clarity, during the term of the Agreement Imir Lab applies the following retention rules to Customer Personal Data, as further described in Schedule 1.B:`,
  },
  {
    kind: "ul-item",
    text: "**Content Data** (prompts, files, completions, and content-derived metadata): retained for no more than **7 days**, then deleted from operational stores. Content Data is **never** used to train, fine-tune, or evaluate Imir Lab's models. These rules apply identically to every account, free or paid.",
  },
  {
    kind: "ul-item",
    text: "**Content Data under Zero Data Retention:** not written to any durable store. Each request is Processed in memory and discarded once the response is returned; the only persisted artefact is an ephemeral, hash-keyed compression index in a memory-only cache, expiring after **24 hours** of inactivity, that contains no readable conversation. Customer may enable ZDR per account or per API key, and may enforce it for its whole organisation, at any time through the Services.",
  },
  {
    kind: "ul-item",
    text: "**Operational metadata** (token counts, latencies, error codes, and similar aggregatable signals, stripped of content-derived identifiers): retained for up to **90 days** for billing, security, and reliability.",
  },
  { kind: "h3", text: "11.3 De-identified data" },
  {
    kind: "p",
    text: `For the avoidance of doubt, Imir Lab may continue to Process information derived from Customer Personal Data that has been de-identified, anonymised, and/or aggregated such that the data is no longer Personal Data under Data Protection Laws, in a manner that does not identify Customer or any Data Subject, for the purpose of improving Imir Lab's systems and services.`,
  },
  { kind: "h3", text: "11.4 Operational logs and backups" },
  {
    kind: "p",
    text: `Operational logs, backups, and archives may be retained beyond the deletion period as part of routine cycling, provided that they are protected by appropriate security measures and deleted in accordance with Imir Lab's retention schedules (typically within **90 days**).`,
  },

  { kind: "h2", text: "12. CCPA-Specific Terms" },
  {
    kind: "p",
    text: `To the extent Imir Lab Processes Customer Personal Data subject to the CCPA on Customer's behalf, Imir Lab is a "**service provider**" as that term is defined in the CCPA, and Imir Lab:`,
  },
  {
    kind: "ul-item",
    text: "will Process such Personal Data only for the business purposes described in this DPA and the Agreement;",
  },
  {
    kind: "ul-item",
    text: "will not Sell or Share (as those terms are defined in the CCPA) such Personal Data;",
  },
  {
    kind: "ul-item",
    text: "will not retain, use, or disclose such Personal Data outside the direct business relationship with Customer, including for combining it with Personal Data from other sources, except as permitted under the CCPA;",
  },
  {
    kind: "ul-item",
    text: "certifies that it understands the foregoing restrictions and will comply with them.",
  },

  { kind: "h2", text: "13. Liability" },
  {
    kind: "p",
    text: `Each party's liability arising out of or in connection with this DPA is subject to the limitations of liability set out in the Agreement. Where the parties are jointly liable to a Data Subject, each party is liable for the proportion of the damage that corresponds to its responsibility.`,
  },

  { kind: "h2", text: "14. General" },
  { kind: "h3", text: "14.1 Order of precedence" },
  {
    kind: "p",
    text: `In the event of a conflict between this DPA and the Agreement, this DPA prevails in matters of data protection. In the event of a conflict between the EU SCCs (or the UK Addendum) and this DPA, the EU SCCs (or UK Addendum) prevail.`,
  },
  { kind: "h3", text: "14.2 Governing law" },
  {
    kind: "p",
    text: `Except as otherwise required by the EU SCCs or applicable Data Protection Laws, this DPA is governed by the **laws of the Republic of Lithuania**, and disputes will be resolved exclusively by the competent courts of **Vilnius, Lithuania**.`,
  },
  { kind: "h3", text: "14.3 Updates" },
  {
    kind: "p",
    text: `Imir Lab may update this DPA from time to time to reflect changes in Data Protection Laws, Sub-Processors, or the Services. Material changes will be communicated to Customer with reasonable advance notice.`,
  },
  { kind: "h3", text: "14.4 Severability" },
  {
    kind: "p",
    text: `If any provision of this DPA is held to be invalid or unenforceable, the remaining provisions remain in effect.`,
  },

  { kind: "h2", text: "Schedule 1 — Details of Processing" },
  { kind: "h3", text: "A. List of Parties" },
  {
    kind: "p",
    text: `**Data exporter / Controller:** The Customer identified in the Agreement.`,
  },
  {
    kind: "p",
    text: `**Data importer / Processor:** UAB Imir Lab, registered office at V. Nagevičiaus 3g, Vilnius, Lithuania, company code 307742103. Contact: **team@condense.chat**.`,
  },
  { kind: "h3", text: "B. Description of Transfer" },
  {
    kind: "p",
    text: `**Categories of Data Subjects whose Personal Data is transferred:**`,
  },
  { kind: "ul-item", text: "Customer's authorised users and personnel;" },
  {
    kind: "ul-item",
    text: "Customer's own end-users, customers, employees, contractors, or other individuals whose Personal Data is contained in prompts, inputs, or files submitted to the Services by Customer.",
  },
  { kind: "p", text: `**Categories of Personal Data transferred:**` },
  {
    kind: "ul-item",
    text: "account and identification data (names, email addresses, user IDs);",
  },
  {
    kind: "ul-item",
    text: "content data: prompts, files, completions, and metadata submitted to the Services by Customer (which may contain any category of Personal Data that Customer chooses to submit);",
  },
  {
    kind: "ul-item",
    text: "usage and technical data: IP addresses, request timestamps, endpoint paths, request/response sizes, token counts, user-agent strings, error logs.",
  },
  {
    kind: "p",
    text: `**Special-category Personal Data:** None expected. Customer must not submit special-category Personal Data, criminal-offence data, or Personal Data of children under 18 to the Services without prior written agreement with Imir Lab.`,
  },
  {
    kind: "p",
    text: `**Frequency of the transfer:** Continuous, for the duration of the Agreement.`,
  },
  {
    kind: "p",
    text: `**Nature of the Processing:** Receipt, compression, temporary storage (or, under Zero Data Retention, in-memory Processing with no durable storage), forwarding to upstream LLM providers selected and configured by Customer, return of responses, and operational logging.`,
  },
  {
    kind: "p",
    text: `**Purpose of the Processing:** Provision of the Services as described in the Agreement.`,
  },
  { kind: "p", text: `**Retention:**` },
  {
    kind: "ul-item",
    text: "**Content Data** (prompts, files, completions, and content-derived metadata): retained for no more than **7 days** for security, debugging, and abuse prevention, then deleted from operational stores. Content Data is **never** used to train, fine-tune, or evaluate Imir Lab's models. The same rule applies to every account, free or paid; aggregated and de-identified derivatives that are no longer Personal Data may be retained indefinitely.",
  },
  {
    kind: "ul-item",
    text: "**Content Data under Zero Data Retention:** not written to any durable store; Processed in memory and discarded once the response is returned. The only persisted artefact is an ephemeral, hash-keyed compression index in a memory-only cache, expiring after **24 hours** of inactivity, that contains no readable conversation. Available to every Customer, per account or per API key, and enforceable organisation-wide.",
  },
  {
    kind: "ul-item",
    text: "**Operational metadata** (token counts, latencies, error codes, and similar aggregatable signals, stripped of content-derived identifiers): retained for up to **90 days** for billing and reliability.",
  },
  {
    kind: "ul-item",
    text: "**Account and billing data:** Retained for the life of the account plus a reasonable post-termination period (typically up to 10 years for accounting records as required by Lithuanian law).",
  },
  { kind: "h3", text: "C. Competent Supervisory Authority" },
  {
    kind: "p",
    text: `The competent Supervisory Authority for the data importer is the **State Data Protection Inspectorate of the Republic of Lithuania (Valstybinė duomenų apsaugos inspekcija)**, vdai.lrv.lt.`,
  },

  { kind: "h2", text: "Schedule 2 — Technical and Organisational Measures" },
  {
    kind: "p",
    text: `Imir Lab implements and maintains the following technical and organisational measures, which it may update from time to time provided the overall level of protection is not materially diminished.`,
  },
  { kind: "h3", text: "1. Pseudonymisation and encryption" },
  {
    kind: "ul-item",
    text: "**In transit:** All Customer Personal Data is encrypted in transit using TLS 1.2 or higher.",
  },
  {
    kind: "ul-item",
    text: "**At rest:** Data stored in cloud infrastructure is encrypted at rest using industry-standard algorithms (AES-256 or equivalent).",
  },
  {
    kind: "ul-item",
    text: "Secrets, API keys, and credentials are stored in dedicated secret-management services and are not committed to source control.",
  },
  {
    kind: "h3",
    text: "2. Confidentiality, integrity, availability, and resilience",
  },
  {
    kind: "ul-item",
    text: "Application infrastructure is deployed on AWS in Europe (Spain, eu-south-2). Requests sent to api.us.condense.chat are processed on AWS in the United States (us-east-1), with conversation content held in memory only. External provider processing locations are described separately in the Subprocessor List.",
  },
  {
    kind: "ul-item",
    text: "Access to production systems is restricted to authorised personnel under the principle of least privilege.",
  },
  {
    kind: "ul-item",
    text: "Authentication to internal systems requires multi-factor authentication.",
  },
  {
    kind: "ul-item",
    text: "Centralised logging and monitoring (Prometheus and Loki) is used to detect anomalous activity.",
  },
  { kind: "h3", text: "3. Restoration of availability" },
  {
    kind: "ul-item",
    text: "Automated backups of critical systems are maintained with documented restoration procedures.",
  },
  {
    kind: "ul-item",
    text: "Infrastructure-as-code allows reconstruction of production environments in case of disaster.",
  },
  {
    kind: "h3",
    text: "4. Process for regularly testing, assessing, and evaluating the effectiveness of measures",
  },
  {
    kind: "ul-item",
    text: "Dependencies are regularly updated and monitored for known vulnerabilities.",
  },
  {
    kind: "ul-item",
    text: "Security incidents are reviewed and remediated through a documented incident-response process.",
  },
  {
    kind: "ul-item",
    text: "Periodic internal reviews of access controls and security configuration.",
  },
  { kind: "h3", text: "5. User identification and authorisation" },
  {
    kind: "ul-item",
    text: "User authentication is handled via Clerk, with industry-standard password storage, optional multi-factor authentication, and audit logging.",
  },
  { kind: "h3", text: "6. Data minimisation" },
  {
    kind: "ul-item",
    text: "Imir Lab collects only the Personal Data necessary to provide the Services.",
  },
  {
    kind: "ul-item",
    text: "For B2B Services, prompts and outputs are not retained after the request is served.",
  },
  { kind: "h3", text: "7. Data quality and limited retention" },
  {
    kind: "ul-item",
    text: "Retention periods are as set out in Schedule 1.B.",
  },
  { kind: "h3", text: "8. Accountability" },
  {
    kind: "ul-item",
    text: "Records of processing activities are maintained under Article 30 GDPR.",
  },
  {
    kind: "ul-item",
    text: "This DPA and the Sub-Processor List are made available to Customer.",
  },
  { kind: "h3", text: "9. Allowing data portability and ensuring erasure" },
  {
    kind: "ul-item",
    text: "Imir Lab provides Customer with the ability to export account data and to delete its account on request.",
  },
  { kind: "h3", text: "10. Sub-Processor management" },
  {
    kind: "ul-item",
    text: "Sub-Processors are subject to written contracts with data-protection obligations no less protective than this DPA.",
  },
  {
    kind: "ul-item",
    text: "The Sub-Processor List in Schedule 3 is maintained and updated as set out in Section 5.",
  },

  { kind: "h2", text: "Schedule 3 — Sub-Processors" },
  {
    kind: "p",
    text: `The Sub-Processor List is maintained at **condense.chat/legal/#doc=subproc** (the "**Subprocessor List**") and forms part of this DPA by reference. Imir Lab will notify Customer of changes to the Subprocessor List in accordance with Section 5.3.`,
  },
  {
    kind: "p",
    text: `The following providers support delivery of the Services. Optional integrations process data only when enabled. Sub-Processor obligations apply where a provider processes Customer Personal Data on Imir Lab's behalf.`,
  },
  {
    kind: "table",
    headers: [
      "Provider / service",
      "Purpose",
      "Processing location",
      "Transfer safeguard",
    ],
    rows: SUBPROCESSOR_ROWS,
  },
  { kind: "p", text: PROVIDER_CONTRACT_NOTICE },
  { kind: "p", text: PROVIDER_LOCATION_NOTICE },
  {
    kind: "p",
    text: UPSTREAM_PROVIDER_NOTICE,
  },

  { kind: "h2", text: "Acceptance" },
  {
    kind: "p",
    text: `This DPA is incorporated into and forms part of the Agreement between Customer and UAB Imir Lab. By using the Services, or by executing an order form that references the Agreement, Customer accepts this DPA. No further signature is required.`,
  },
  {
    kind: "p",
    text: `For any questions about this DPA, contact **team@condense.chat**.`,
  },
];

const AUP_VERSION = "1.0";
const AUP_EFFECTIVE = "May 27, 2026";

export const AUP_BLOCKS: Block[] = [
  { kind: "h1", text: "Acceptable Use Policy" },
  { kind: "meta", text: `Version ${AUP_VERSION} · Effective ${AUP_EFFECTIVE}` },

  {
    kind: "p",
    text: `This Acceptable Use Policy (the "**AUP**") sets out the rules for using **condense.chat** and any related APIs, applications, and websites (the "**Service**") operated by **UAB Imir Lab** ("**Imir Lab**", "**we**", or "**us**"). It is incorporated into our Terms of Service and forms part of your agreement with us.`,
  },
  {
    kind: "p",
    text: `You are responsible for complying with this AUP, and for ensuring that anyone who uses the Service under your account or on your behalf complies with it.`,
  },
  {
    kind: "p",
    text: `We may suspend or terminate your access to the Service, immediately and without notice, if we reasonably believe you have violated this AUP. In serious cases — including illegal content, ongoing abuse, or threats to the security or integrity of the Service — we may also report the conduct to law enforcement or to affected third parties.`,
  },

  { kind: "h2", text: "You must not use the Service to:" },

  { kind: "h3", text: "1. Break the law" },
  {
    kind: "ul-item",
    text: "Violate any applicable law, regulation, or sanctions regime, including export controls and economic sanctions imposed by the EU, US, UK, UN, or any other relevant jurisdiction.",
  },
  {
    kind: "ul-item",
    text: "Infringe the intellectual property, privacy, publicity, contractual, or other rights of any person.",
  },

  { kind: "h3", text: "2. Generate or process harmful content" },
  {
    kind: "ul-item",
    text: "Generate, transmit, store, or process child sexual abuse material (CSAM) or any content that sexually exploits or endangers minors.",
  },
  {
    kind: "ul-item",
    text: "Generate content that incites or promotes violence, terrorism, genocide, or other serious harm to people.",
  },
  {
    kind: "ul-item",
    text: "Generate non-consensual intimate imagery, deepfakes designed to harass or defame real people, or doxxing content.",
  },
  {
    kind: "ul-item",
    text: "Generate malware, ransomware, exploits, or other code designed to damage systems or steal data.",
  },

  { kind: "h3", text: "3. Evade safety, abuse, or fraud measures" },
  {
    kind: "ul-item",
    text: "Use the Service to bypass, jailbreak, or evade the safety, content, or usage policies of any upstream LLM provider you route through it (including Anthropic's Usage Policies, OpenAI's Usage Policies, Google's policies, and equivalents).",
  },
  {
    kind: "ul-item",
    text: "Use the Service to manipulate or circumvent moderation systems, age checks, or identity verification on any platform.",
  },
  {
    kind: "ul-item",
    text: "Use the Service to send spam, phishing, scam, or other deceptive communications at scale.",
  },
  {
    kind: "ul-item",
    text: "Use the Service to commit or facilitate fraud, money laundering, or other financial crime.",
  },

  { kind: "h3", text: "4. Attack the Service" },
  {
    kind: "ul-item",
    text: "Probe, scan, or test the vulnerability of the Service, or breach security or authentication measures, except as part of a coordinated vulnerability disclosure with our prior written consent.",
  },
  {
    kind: "ul-item",
    text: "Interfere with or disrupt the integrity or performance of the Service, including by sending automated traffic outside documented rate limits, by attempting denial-of-service attacks, or by interfering with other customers' use.",
  },
  {
    kind: "ul-item",
    text: "Use credentials, API keys, or accounts that you are not authorised to use.",
  },

  { kind: "h3", text: "5. Take what isn't yours" },
  {
    kind: "ul-item",
    text: "Reverse-engineer, decompile, disassemble, or attempt to extract the source code, model weights, or compression algorithms of the Service.",
  },
  {
    kind: "ul-item",
    text: "Use the Service to develop a competing product, train a competing compression model, or benchmark the Service for the purposes of building a substitute service.",
  },
  {
    kind: "ul-item",
    text: "Scrape or harvest content, prompts, completions, or other data from the Service in a manner that is not part of normal usage.",
  },

  { kind: "h3", text: "6. Resell without permission" },
  {
    kind: "ul-item",
    text: "Resell, sublicense, or otherwise make the Service available to third parties **without our prior written consent**.",
  },
  {
    kind: "ul-item",
    text: `"B2B2C" embedding — that is, embedding the Service inside your own product offered to your end-users — is permitted **only** to the extent expressly authorised under your business order form or written agreement with us. If in doubt, ask us at **team@condense.chat** before you ship.`,
  },

  { kind: "h3", text: "7. Endanger people" },
  {
    kind: "ul-item",
    text: "Use the Service in any application where errors, omissions, or distortions in compression could cause harm to people, property, finances, or rights, including medical, psychiatric, or mental-health decisions; legal advice provided to end-users; safety-critical systems; financial trading or credit decisions; employment decisions; or any other high-risk application. If you choose to use the Service for such purposes, you do so entirely at your own risk and on your own consideration. We provide **no safety guarantee** for high-risk uses.",
  },

  { kind: "h2", text: "Reporting abuse" },
  {
    kind: "p",
    text: `If you become aware of conduct that violates this AUP, email us at **team@condense.chat**. Include enough detail for us to identify and investigate the issue.`,
  },

  { kind: "h2", text: "Changes" },
  {
    kind: "p",
    text: `We may update this AUP from time to time. Material changes will be communicated by email or through the Service with reasonable advance notice. Continued use of the Service after the effective date of an update constitutes acceptance of the updated AUP.`,
  },

  { kind: "h2", text: "Contact" },
  { kind: "contact" },
];

const SUBPROC_LAST_UPDATED = VENDOR_REVIEW_DATE;

export const SUBPROC_BLOCKS: Block[] = [
  { kind: "h1", text: "Subprocessor List" },
  {
    kind: "meta",
    text: `Status: current · Last updated ${SUBPROC_LAST_UPDATED}`,
  },

  {
    kind: "p",
    text: `This page lists the sub-processors engaged by **UAB Imir Lab** ("**Imir Lab**") to provide **condense.chat** and related services. A sub-processor is a third party that processes personal data on our behalf.`,
  },
  {
    kind: "p",
    text: `This list is incorporated by reference into our Privacy Policy and our Data Processing Addendum, and we keep it current. To get notified of changes — including before a new sub-processor goes live — email **team@condense.chat** with the subject line "Subprocessor updates" and we'll add you to the notification list.`,
  },

  { kind: "h2", text: "Service providers and sub-processors" },
  {
    kind: "p",
    text: "Optional integrations process data only when enabled. A provider's role depends on the processing it performs; providers processing Customer Personal Data on our behalf are subject to our DPA's subprocessor obligations.",
  },
  {
    kind: "table",
    headers: [
      "Provider / service",
      "Purpose",
      "Processing location",
      "Transfer safeguard",
    ],
    rows: SUBPROCESSOR_ROWS,
  },
  { kind: "p", text: PROVIDER_CONTRACT_NOTICE },
  { kind: "p", text: PROVIDER_LOCATION_NOTICE },

  { kind: "h2", text: "Additional operational and website providers" },
  {
    kind: "p",
    text: "These services support operations, software delivery or website interactions. Their inclusion does not mean each receives your prompts or acts as a subprocessor for every use. Linked booking and community services receive information you choose to submit to them.",
  },
  {
    kind: "table",
    headers: ["Provider / service", "Purpose", "Context"],
    rows: OPERATIONAL_VENDOR_ROWS,
  },

  {
    kind: "h2",
    text: "Customer-selected upstream LLM providers",
  },
  {
    kind: "p",
    text: UPSTREAM_PROVIDER_NOTICE,
  },
  {
    kind: "p",
    text: `You are responsible for selecting your upstream LLM provider and for complying with its policies.`,
  },

  { kind: "h2", text: "How we add, change, or remove sub-processors" },
  {
    kind: "p",
    text: `Before we add a new sub-processor or replace an existing one, we publish the change here and, for business customers, send advance notice as described in Section 5.3 of the Data Processing Addendum (**30 days** by default). If you are a business customer and have a reasonable data-protection objection to a new sub-processor, you may raise it with us within the notice period at **team@condense.chat**.`,
  },

  { kind: "h2", text: "Change log" },
  {
    kind: "table",
    headers: ["Date", "Change"],
    rows: [
      [
        TRANSFER_UPDATE_DATE,
        "Added AWS United States (us-east-1) processing for requests sent to api.us.condense.chat. Stated each provider's processing location and transfer safeguard. Added Slack (operational alerts, incident reports and dashboards), replacing Discord.",
      ],
      [
        VENDOR_REVIEW_DATE,
        "Updated AWS hosting to Europe (Spain), email delivery to Google and Stripe billing status. Added Condense-managed Anthropic processing and operational providers. Clarified provider processing locations, customer-selected upstreams and backend PostHog events. Replaced the Hostinger email entry with Google.",
      ],
      [
        "June 20, 2026",
        "Added PostHog, Inc. (consent-gated product and website analytics, PostHog Cloud EU).",
      ],
      [
        "June 11, 2026",
        "Updated sub-processor contracting entities and confirmed location of processing as EU: AWS (EMEA) S.à r.l., Google Cloud EMEA Limited (including the Gemini API), Clerk, Stripe Payments Europe, and Hostinger UAB.",
      ],
      [
        "June 8, 2026",
        "Removed Datadog and Plausible (not in use; monitoring is Prometheus and Loki). Added Hostinger UAB (transactional email) and Google Gemini API (incident-response log analysis).",
      ],
      ["May 27, 2026", "Initial publication."],
    ],
  },

  { kind: "h2", text: "Contact" },
  { kind: "contact" },
];

const COOKIES_VERSION = "1.4";
const COOKIES_EFFECTIVE = VENDOR_REVIEW_DATE;

export const COOKIES_BLOCKS: Block[] = [
  { kind: "h1", text: "Cookie Disclosure" },
  {
    kind: "meta",
    text: `Version ${COOKIES_VERSION} · Effective ${COOKIES_EFFECTIVE}`,
  },

  {
    kind: "p",
    text: `We keep cookies simple. This page tells you what we use, why, how long it lasts, and who receives it. No advertising, no cross-site tracking, no selling cookie data. Browser analytics and session replay are optional and off until you choose them.`,
  },

  { kind: "h2", text: "What we use" },
  {
    kind: "p",
    text: `**Strictly necessary cookies** — always on, because the Service can't work without them:`,
  },
  {
    kind: "ul-item",
    text: "**Authentication** — to keep you signed in to your account. These cookies are set by our identity provider, Clerk, and by our own session management. Without them, the Service wouldn't work.",
  },
  {
    kind: "ul-item",
    text: "**Security** — to protect your session from hijacking, CSRF attacks, and similar threats.",
  },
  {
    kind: "ul-item",
    text: "**Consent preferences** — a single cookie (`cc_cookie_preferences`) that remembers whether you allowed analytics and session replay, so we can apply your choice across condense.chat subdomains.",
  },
  {
    kind: "p",
    text: `Under EU ePrivacy rules, strictly necessary cookies do not require consent.`,
  },
  {
    kind: "p",
    text: `**Analytics cookies (optional)** — if you allow analytics, we use **PostHog** to understand how people use the marketing site and app. PostHog recognises a browser across visits and condense.chat subdomains and receives page views, referrers, source IP and derived location, browser and device information, feature usage, clicks, and scrolling. After sign-in, the browser history is associated with the account's user ID, email, and name so we can measure visit-to-account conversion.`,
  },
  {
    kind: "p",
    text: `**Session replay (optional)** — this is a separate choice and is not enabled by analytics consent alone. If you allow it, PostHog receives a privacy-masked visual reconstruction of the pages you use. Replay requires analytics to be enabled. Form values are masked, email-like page text is redacted, one-time secrets are excluded, and URL query strings and fragments are removed before replay capture.`,
  },
  {
    kind: "table",
    headers: [
      "Cookie",
      "Category and purpose",
      "Duration",
      "Who can receive it",
    ],
    rows: [
      [
        "`cc_cookie_preferences`",
        "Strictly necessary — stores the versioned analytics and replay choices.",
        "180 days",
        "UAB Imir Lab only",
      ],
      [
        "`__session` and Clerk authentication cookies",
        "Strictly necessary — authenticate the account, maintain the signed-in session, and protect it.",
        "`__session` is short-lived (about 60 seconds and refreshed); other Clerk session cookies follow the configured session lifetime and browser limits.",
        "UAB Imir Lab and Clerk as authentication processor",
      ],
      [
        "`ph_<project-token>_posthog`",
        "Optional analytics — browser identifier, session and attribution data; also supports replay only when replay is separately allowed.",
        "Up to 365 days",
        "UAB Imir Lab and PostHog as analytics processor (PostHog Cloud EU)",
      ],
    ],
  },
  {
    kind: "p",
    text: `The PostHog cookie is first-party and shared only among our condense.chat subdomains; it is not available to unrelated websites. Analytics requests pass through our first-party relay to PostHog Cloud EU. If you choose **just essentials**, PostHog is not initialised on later page loads; if it was already running, capture and persistence are disabled immediately.`,
  },

  {
    kind: "p",
    text: "Cookie choices govern browser analytics and replay. Separately, the backend sends account-registration events (which can include email and account identifiers) and links identifiers in PostHog independently of that choice. These events do not set analytics cookies or enable replay. See Privacy Policy Section 3.5.",
  },

  { kind: "h2", text: "What we don't use" },
  {
    kind: "ul-item",
    text: "**No advertising cookies.** We don't run ads and we don't share data with ad networks.",
  },
  {
    kind: "ul-item",
    text: "**No cross-site tracking.** We don't follow you around the web.",
  },
  {
    kind: "ul-item",
    text: "**No browser analytics or replay without consent.** PostHog loads in the browser only after analytics consent, and replay starts only after the separate replay choice.",
  },
  {
    kind: "ul-item",
    text: "**No social media trackers.** No Facebook Pixel, no LinkedIn Insight Tag, no TikTok Pixel.",
  },

  { kind: "h2", text: "Your browser controls" },
  {
    kind: "p",
    text: `Use the **Cookie settings** button below to change or withdraw your optional choices at any time. Withdrawing analytics immediately stops analytics and replay in that browser and disables PostHog persistence. You can also block or delete cookies through your browser settings. If you block strictly necessary authentication cookies, you won't be able to sign in to the Service.`,
  },

  { kind: "h2", text: "Changes" },
  {
    kind: "p",
    text: `If we ever start using a cookie outside the categories described here — for example, an advertising cookie — we will update this page and ask for your consent through the banner **before** the cookie is set. We will not retroactively apply new non-essential cookies to existing visitors without consent.`,
  },

  { kind: "h2", text: "Related documents" },
  {
    kind: "ul-item",
    text: "**Privacy Policy** — what personal data we collect and how we use it.",
  },
  {
    kind: "ul-item",
    text: "**Subprocessor List** — the third parties (including Clerk) that process data on our behalf.",
  },

  { kind: "h2", text: "Contact" },
  { kind: "contact" },
];

export type LegalDoc = {
  key: string;

  title: string;

  version: string;

  updated: string;

  desc: string;

  blocks: Block[];
};

export const LEGAL: LegalDoc[] = [
  {
    key: "tos",
    title: "Terms of Service",
    version: `v${TOS_VERSION}`,
    updated: "2026-09-25",
    desc: "What you agree to by using condense.",
    blocks: TOS_BLOCKS,
  },

  {
    key: "privacy",
    title: "Privacy Policy",
    version: `v${PRIVACY_VERSION}`,
    updated: "2026-09-25",
    desc: "What we collect, why, and how to get it deleted.",
    blocks: PRIVACY_BLOCKS,
  },

  {
    key: "dpa",
    title: "Data Processing Addendum",
    version: `v${DPA_VERSION}`,
    updated: "2026-09-25",
    desc: "For business customers — GDPR Art. 28 / SCCs, Zero Data Retention.",
    blocks: DPA_BLOCKS,
  },

  {
    key: "aup",
    title: "Acceptable Use Policy",
    version: "v1.0",
    updated: "2026-05-27",
    desc: "What you can't do (spam, abuse, evading safety).",
    blocks: AUP_BLOCKS,
  },

  {
    key: "subproc",
    title: "Subprocessor List",
    version: "current",
    updated: "2026-09-25",
    desc: "Hosting, authentication, billing, AI processing, analytics and operational providers.",
    blocks: SUBPROC_BLOCKS,
  },

  {
    key: "cookies",
    title: "Cookie Disclosure",
    version: `v${COOKIES_VERSION}`,
    updated: "2026-09-25",
    desc: "Strictly necessary cookies, plus optional consent-gated browser analytics and session replay.",
    blocks: COOKIES_BLOCKS,
  },
];
